import React, { useState } from 'react';
import { Search, X, Plus } from 'lucide-react';
import Swal from 'sweetalert2';

// Mock API_BASE_URL for demo
import API_BASE_URL from "../config";

const POReceiving = () => {
    const [poNumber, setPoNumber] = useState('');
    const [poData, setPoData] = useState(null);
    const [poDetails, setPoDetails] = useState([]);
    const [drList, setDrList] = useState([]);
    const [invoiceList, setInvoiceList] = useState([]);
    const [showDRModal, setShowDRModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [allPOHeaders, setAllPOHeaders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    const [drForm, setDrForm] = useState({
        drNo: '',
        drDate: '',
        drAmount: ''
    });

    const [invoiceForm, setInvoiceForm] = useState({
        invoiceNo: '',
        invoiceDate: '',
        invoiceAmount: ''
    });


    const openSearchModal = async () => {
        setShowSearchModal(true);
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/T_PO_Header`);
            const data = await res.json();
            setAllPOHeaders(data);
        } catch (error) {
            console.error('Error fetching PO headers:', error);
            alert('Error loading PO list');
        } finally {
            setLoading(false);
        }
    };

   const addInvoiceDetail = async () => {
    if (!invoiceForm.invoiceNo || !invoiceForm.invoiceDate || !invoiceForm.invoiceAmount) {
        Swal.fire({
            icon: 'warning',
            title: 'Incomplete Fields',
            text: 'Please fill all invoice fields',
            confirmButtonColor: '#3085d6'
        });
        return;
    }

    if (!poData) {
        Swal.fire({
            icon: 'info',
            title: 'No PO Selected',
            text: 'Please select a PO first',
            confirmButtonColor: '#3085d6'
        });
        return;
    }

    try {
        Swal.fire({
            title: 'Saving Invoice...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        const payload = {
            poHeaderId: poData.id,
            invoiceNo: invoiceForm.invoiceNo,
            invoiceDate: new Date(invoiceForm.invoiceDate).toISOString(),
            invoiceAmount: parseFloat(invoiceForm.invoiceAmount)
        };

        const res = await fetch(`${API_BASE_URL}api/T_PO_Invoice_Details`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            throw new Error('Failed to add invoice');
        }

        const newInvoice = await res.json();

        Swal.close();

        setInvoiceList(prev => [...prev, newInvoice]);
        setInvoiceForm({ invoiceNo: '', invoiceDate: '', invoiceAmount: '' });
        setShowInvoiceModal(false);

        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Invoice added successfully!',
            timer: 2000,
            showConfirmButton: false
        });

    } catch (error) {
        console.error('Error adding invoice:', error);

        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error adding invoice. Please try again.',
            confirmButtonColor: '#d33'
        });
    }
};

const selectPO = async (po) => {
        setPoData(po);
        setPoNumber(po.poNumber);
        setShowSearchModal(false);
        setSearchTerm('');

        // Clear previous data immediately
        setPoDetails([]);
        setDrList([]);
        setInvoiceList([]);

        setLoading(true);
        try {
            const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/get/${po.id}`);
            const details = await detailsRes.json();
            setPoDetails(details);

            const drRes = await fetch(`${API_BASE_URL}api/T_PO_DR_Details`);
            if (drRes.ok) {
                const allDRs = await drRes.json();
                setDrList(allDRs.filter(dr => dr.poHeaderId === po.id));
            }

            // Fetch invoices for the selected PO
            const invoiceRes = await fetch(`${API_BASE_URL}api/T_PO_Invoice_Details/${po.id}`);
            if (invoiceRes.ok) {
                const invoices = await invoiceRes.json();
                setInvoiceList(invoices);
            }

        } catch (error) {
            console.error('Error fetching PO details:', error);
            alert('Error loading PO data');
        } finally {
            setLoading(false);
        }
    };

    // FIXED: Handle null/undefined poNumber values
    const filteredPOs = allPOHeaders.filter(po =>
        (po.poNumber || '').toLowerCase().includes((searchTerm || '').toLowerCase())
    );

 const addDRDetail = async () => {
    if (!drForm.drNo || !drForm.drDate || !drForm.drAmount) {
        Swal.fire({
            icon: 'warning',
            title: 'Incomplete Fields',
            text: 'Please fill all DR fields',
            confirmButtonColor: '#3085d6'
        });
        return;
    }

    try {
        const payload = [{
            poHeaderId: poData.id,
            drNo: drForm.drNo,
            drDate: new Date(drForm.drDate).toISOString(),
            drAmount: parseFloat(drForm.drAmount)
        }];

        const res = await fetch(`${API_BASE_URL}api/T_PO_DR_Details/bulk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });

        if (!res.ok) {
            throw new Error('Failed to add DR');
        }

        const newDR = await res.json();

        setDrList(prev => [...prev, ...newDR]);
        setDrForm({ drNo: '', drDate: '', drAmount: '' });
        setShowDRModal(false);

        Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'DR added successfully!',
            timer: 2000,
            showConfirmButton: false
        });

    } catch (error) {
        console.error('Error adding DR:', error);

        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error adding DR. Please try again.',
            confirmButtonColor: '#d33'
        });
    }
};


const deleteDR = async (id) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This DR will be permanently deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
        const res = await fetch(`${API_BASE_URL}api/T_PO_DR_Details/${id}`, {
            method: 'DELETE'
        });

        if (!res.ok) {
            throw new Error('Delete failed');
        }

        setDrList(prev => prev.filter(dr => dr.id !== id));

        Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'DR deleted successfully.',
            timer: 1500,
            showConfirmButton: false
        });

    } catch (error) {
        console.error('Error deleting DR:', error);

        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error deleting DR. Please try again.',
            confirmButtonColor: '#d33'
        });
    }
};
const deleteInvoice = async () => {
    if (!poData?.id) {
        Swal.fire({
            icon: 'info',
            title: 'No PO Selected',
            text: 'Please select a PO first'
        });
        return;
    }

    const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'All invoices under this PO will be deleted.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete all',
        cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
        Swal.fire({
            title: 'Deleting invoices...',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const res = await fetch(
            `${API_BASE_URL}api/T_PO_Invoice_Details/po/${poData.id}`,
            { method: 'DELETE' }
        );

        if (!res.ok) {
            throw new Error('Delete failed');
        }

        Swal.close();

        setInvoiceList([]); // since bulk delete by POHeaderId

        Swal.fire({
            icon: 'success',
            title: 'Deleted',
            text: 'Invoices deleted successfully!',
            timer: 2000,
            showConfirmButton: false
        });

    } catch (error) {
        console.error('Error deleting invoice:', error);

        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Error deleting invoices. Please try again.'
        });
    }
};

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-GB');
    };

    return (
        <div className="min-h-screen  from-slate-50 to-blue-100 p-6">
            <div className="max-w-8xl mx-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 shadow-lg mb-6 backdrop-blur-sm bg-opacity-95">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Search className="w-7 h-7" />
                        PO Receiving
                    </h1>
                </div>

                {/* Search Section */}
                <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg p-6 mb-6 border border-white/20">
                    <div className="flex gap-4 items-end">
                        <div className="flex-1">
                            <label className="block text-xs font-semibold text-gray-700 mb-2">
                                Search PO Number:
                            </label>
                            <input
                                type="text"
                                value={poNumber}
                                readOnly
                                placeholder="Click search button to find a PO"
                                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>
                        <button
                            onClick={openSearchModal}
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            <Search className="w-5 h-5" />
                            Search PO
                        </button>
                    </div>
                </div>
                
                {poData && (
                    <div className="bg-white/70 backdrop-blur-lg shadow-lg rounded-xl overflow-hidden border border-white/30 mb-6">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <h2 className="text-xl font-semibold text-white">Purchase Order Information</h2>
                        </div>

                        <div className="grid grid-cols-2 gap-8 p-6">
                            <div className="space-y-4">
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">PO Number:</span>
                                    <span className="text-blue-700 font-semibold text-sm">{poData.poNumber}</span>
                                </div>
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">PO Date:</span>
                                    <span className="text-gray-800 text-sm">{formatDate(poData.poCreatedDate)}</span>
                                </div>
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">Vendor Name:</span>
                                    <span className="text-blue-700 font-semibold text-sm">TECHNOTRONIX COMPUTER SERVICES</span>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">Charged To:</span>
                                    <span className="text-blue-700 font-semibold text-sm">BENBY ENTERPRISES INC.</span>
                                </div>
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">Payment Terms:</span>
                                    <span className="text-gray-800 text-sm">{poData.paymentTerms}</span>
                                </div>
                                <div className="flex pb-3 border-b border-gray-200">
                                    <span className="font-medium text-gray-600 w-36 text-sm">Total Amount:</span>
                                    <span className="text-gray-800 font-semibold text-sm">₱{poDetails.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                        </div>

                        {/* PO Details Table */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3">
                            <h3 className="font-semibold text-base">PO Receiving Details</h3>
                        </div>
                        <div className="overflow-x-auto p-6">
                            <table className="w-full text-sm bg-white/50 rounded-lg overflow-hidden">
                                <thead className="bg-blue-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">Quantity</th>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">UOM</th>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">Item Description</th>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">Server Status</th>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">Last Served Date</th>
                                        <th className="px-4 py-3 text-left font-semibold text-blue-900 text-xs">Qty Received</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {poDetails.map((item, idx) => (
                                        <tr key={idx} className="border-b border-gray-100 hover:bg-blue-50/50">
                                            <td className="px-4 py-3">{item.qty}</td>
                                            <td className="px-4 py-3">{item.uom}</td>
                                            <td className="px-4 py-3">{item.itemDescription}</td>
                                            <td className="px-4 py-3">
                                                <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" value={item.serveStatus || ''} readOnly>
                                                    <option>{item.serveStatus}</option>
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                <input type="date" value={item.lastServedDate?.split('T')[0] || ''} readOnly className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white" />
                                            </td>
                                            <td className="px-4 py-3">
                                                <input type="number" value={item.totalReceivedQty || 0} readOnly className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold text-blue-700 text-sm bg-white" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                
                {/* Always Display Forms Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Delivery Details Card */}
                    <div className="bg-white/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/30 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4">
                            <h3 className="font-semibold text-lg">Delivery Details</h3>
                        </div>

                        <div className="p-6">
                            <button
                                onClick={() => setShowDRModal(true)}
                                disabled={!poData}
                                className="w-full bg-blue-500 hover:bg-blue-600 text-white px-5 py-3.5 rounded-lg font-medium flex items-center justify-center gap-3 transition-all shadow-md mb-5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-5 h-5" />
                                Add Delivery Receipt
                            </button>

                            <div className="bg-white/50 rounded-lg p-4 border border-gray-200">
                                <table className="w-full text-sm">
                                    <thead className="bg-blue-50 border-b border-blue-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">DR Number</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">DR Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">DR Amount</th>
                                            <th className="px-4 py-3 w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {drList.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="px-4 py-8 text-center text-gray-400 text-sm">
                                                    No delivery receipts added yet
                                                </td>
                                            </tr>
                                        ) : (
                                            drList.map((dr) => (
                                                <tr key={dr.id} className="border-b border-gray-100 hover:bg-blue-50/50">
                                                    <td className="px-4 py-3">{dr.drNo}</td>
                                                    <td className="px-4 py-3">{formatDate(dr.drDate)}</td>
                                                    <td className="px-4 py-3 font-semibold text-blue-700">₱{parseFloat(dr.drAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                    <td className="px-4 py-3">
                                                        <button onClick={() => deleteDR(dr.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded">
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Invoice Details Card */}
                    <div className="bg-white/70 backdrop-blur-lg rounded-xl shadow-lg border border-white/30 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-4">
                            <h3 className="font-semibold text-lg">Invoice Details</h3>
                        </div>

                        <div className="p-6">
                            <button
                                onClick={() => setShowInvoiceModal(true)}
                                disabled={!poData}
                                className="w-full bg-blue-500 hover:bg-blue-700 text-white px-5 py-3.5 rounded-lg font-medium flex items-center justify-center gap-3 transition-all shadow-md mb-5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="w-5 h-5" />
                                Add Invoice Details
                            </button>

                            <div className="bg-white/50 rounded-lg p-4 border border-gray-200">
                                <table className="w-full text-sm">
                                    <thead className="bg-blue-50 border-b border-blue-200">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">Invoice Number</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">Invoice Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-blue-900">Invoice Amount</th>
                                            <th className="px-4 py-3 w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoiceList.length === 0 ? (
                                            <tr>
                                                <td colSpan="4" className="px-4 py-8 text-center text-gray-400 text-sm">
                                                    No invoices added yet
                                                </td>
                                            </tr>
                                        ) : (
                                            invoiceList.map((inv) => (
                                                <tr key={inv.id} className="border-b border-gray-100 hover:bg-blue-50/50">
                                                    <td className="px-4 py-3">{inv.invoiceNo}</td>
                                                    <td className="px-4 py-3">{formatDate(inv.invoiceDate)}</td>
                                                    <td className="px-4 py-3 font-semibold text-blue-700">₱{parseFloat(inv.invoiceAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                    <td className="px-4 py-3">
                                                        <button onClick={() => deleteInvoice(inv.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1.5 rounded">
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* DR Modal */}
                {showDRModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border border-white/20">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
                                <h3 className="text-lg font-semibold">Add Delivery Receipt</h3>
                                <button onClick={() => setShowDRModal(false)} className="hover:bg-white/20 rounded-lg p-1.5 transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-5">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">DR Number</label>
                                    <input
                                        type="text"
                                        value={drForm.drNo}
                                        onChange={(e) => setDrForm({ ...drForm, drNo: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                        placeholder="Enter DR number"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">DR Date</label>
                                    <input
                                        type="date"
                                        value={drForm.drDate}
                                        onChange={(e) => setDrForm({ ...drForm, drDate: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">DR Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={drForm.drAmount}
                                        onChange={(e) => setDrForm({ ...drForm, drAmount: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                        placeholder="0.00"
                                    />
                                </div>

                                <button
                                    onClick={addDRDetail}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg font-medium flex items-center justify-center gap-3 shadow-md transition-all"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add Delivery Receipt
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Invoice Modal */}
                {showInvoiceModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border border-white/20">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 rounded-t-xl flex justify-between items-center">
                                <h3 className="text-lg font-semibold">Add Invoice Details</h3>
                                <button onClick={() => setShowInvoiceModal(false)} className="hover:bg-white/20 rounded-lg p-1.5 transition-all">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-5">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">Invoice Number</label>
                                    <input
                                        type="text"
                                        value={invoiceForm.invoiceNo}
                                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceNo: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                        placeholder="Enter invoice number"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">Invoice Date</label>
                                    <input
                                        type="date"
                                        value={invoiceForm.invoiceDate}
                                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceDate: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-2">Invoice Amount</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={invoiceForm.invoiceAmount}
                                        onChange={(e) => setInvoiceForm({ ...invoiceForm, invoiceAmount: e.target.value })}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                        placeholder="0.00"
                                    />
                                </div>
                                <button
                                    onClick={addInvoiceDetail}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-lg font-medium flex items-center justify-center gap-3 shadow-md transition-all"
                                >
                                    <Plus className="w-5 h-5" />
                                    Add Invoice
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Search PO Modal */}
                {showSearchModal && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-white/20">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 rounded-t-xl flex justify-between items-center">
                                <h3 className="text-lg font-semibold text-white">Search Purchase Order</h3>
                                <button
                                    onClick={() => {
                                        setShowSearchModal(false);
                                        setSearchTerm('');
                                    }}
                                    className="hover:bg-white/20 rounded-lg p-1.5 transition-all"
                                >
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            <div className="p-6">
                                <label className="block text-xs font-semibold text-gray-700 mb-2">
                                    Search PO Number
                                </label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Type to search..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm transition-all"
                                    autoFocus
                                />
                            </div>

                            <div className="flex-1 overflow-y-auto px-6 pb-6">
                                {loading ? (
                                    <div className="text-center py-12">
                                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                                        <p className="text-gray-500 mt-4 text-sm font-medium">Loading PO list...</p>
                                    </div>
                                ) : filteredPOs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 text-sm font-medium">No purchase orders found</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {filteredPOs.map((po) => (
                                            <button
                                                key={po.id}
                                                onClick={() => selectPO(po)}
                                                className="w-full text-left px-5 py-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50/50 transition-all backdrop-blur-sm bg-white/50"
                                            >
                                                <div className="font-semibold text-blue-700 mb-2 text-sm">
                                                    [{po.poNumber}] - TECHNOTRONIX COMPUTER SERVICES
                                                </div>
                                                <div className="text-xs text-gray-600 space-x-4">
                                                    <span><span className="font-semibold">Date:</span> {formatDate(po.poCreatedDate)}</span>
                                                    <span>•</span>
                                                    <span><span className="font-semibold">Payment:</span> {po.paymentTerms}</span>
                                                    <span>•</span>
                                                    <span><span className="font-semibold">Ship To:</span> {po.shipTo_Location}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default POReceiving;